import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { selectFranchise } from '../redux/slices/salonSlice';
import FranchiseCard from '../components/FranchiseCard';
import type { FranchiseData } from '../utils/franchiseStorage';
import navigationData from '../data/navigation.json';
import footerData from '../data/footer.json';
import Navigation from '../components/Navigation/Navigation';
import Footer from '../components/Footer/Footer';

/**
 * FranchiseSelectionPage
 * Displays list of franchises for user to select
 */
const FranchiseSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const { franchises, loading, error, selectedFranchise } = useAppSelector(
    (state) => state.salon
  );

  // Redirect if franchise already selected
  useEffect(() => {
    if (selectedFranchise) {
      navigate('/');
    }
  }, [selectedFranchise, navigate]);

  // Redirect if no franchises
  useEffect(() => {
    if (!loading && franchises.length === 0) {
      navigate('/');
    }
  }, [loading, franchises, navigate]);

  const handleSelectFranchise = (franchise: FranchiseData) => {
    dispatch(selectFranchise(franchise));
    // Navigation will happen automatically via useEffect above
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading locations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-md mx-auto text-center">
            <span className="material-symbols-outlined text-red-500 text-6xl mb-4">
              error
            </span>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Error Loading Locations
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navigation data={navigationData} />
      
      <main className="container mx-auto px-4 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Select Your Location
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Choose the salon location nearest to you for the best experience
          </p>
        </motion.div>

        {/* Franchise Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {franchises.map((franchise, index) => (
            <motion.div
              key={franchise.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <FranchiseCard
                franchise={franchise}
                onSelect={handleSelectFranchise}
                variant="default"
              />
            </motion.div>
          ))}
        </div>

        {/* Empty State */}
        {franchises.length === 0 && !loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <span className="material-symbols-outlined text-gray-400 text-6xl mb-4">
              location_off
            </span>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              No Locations Available
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              We couldn't find any locations for this salon.
            </p>
          </motion.div>
        )}
      </main>
      
      <Footer data={footerData} />
    </div>
  );
};

export default FranchiseSelectionPage;
